import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from "@reduxjs/toolkit";
import * as categoriesApi from "@/api/modules/categories";
import { normalizeData, normalizeIncluded } from "@/utils/jsonapi";

/**
 * Entity adapter for normalized category state
 * Provides built-in CRUD reducers and selectors
 */
const categoriesAdapter = createEntityAdapter({
  // Assumes entities have an 'id' field (default)
  selectId: (category) => category.id,
  // Keep sorted by sort_order, then name
  sortComparer: (a, b) => {
    if (a.sort_order !== b.sort_order) {
      return (a.sort_order || 999) - (b.sort_order || 999);
    }
    return (a.name || '').localeCompare(b.name || '');
  },
});

/**
 * Async thunk to fetch all categories
 * Normalizes JSON:API response and processes included resources
 *
 * @param {Object} [queryOptions] - JSON:API query options
 * @param {Object} [queryOptions.filters] - Filter criteria (e.g., { name: { icontains: 'worship' } })
 * @param {Array<string>|string} [queryOptions.sort] - Sort fields (e.g., ['name'])
 * @param {Array<string>|string} [queryOptions.include] - Relations to include
 * @param {number} [queryOptions.page] - Page number
 * @param {number} [queryOptions.pageSize] - Items per page
 */
export const fetchCategoriesThunk = createAsyncThunk(
  "categories/fetchAll",
  async (queryOptions = {}, { rejectWithValue }) => {
    try {
      const response = await categoriesApi.fetchCategories(queryOptions);

      // Handle DRF pagination format: { count, next, previous, results: [...] }
      // The results array contains JSON:API formatted resources
      const results = response.results || response.data || [];

      // Normalize each JSON:API resource in the results array
      const normalized = normalizeData(results);

      // Process any included resources (dispatch to their slices)
      const included = normalizeIncluded(response.included || []);

      return {
        data: normalized || [],
        count: response.count,
        next: response.next,
        previous: response.previous
      };
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to create a new category
 * Normalizes JSON:API response and processes included resources
 */
export const createCategoryThunk = createAsyncThunk(
  "categories/create",
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await categoriesApi.createCategory(data);

      // Process any included resources
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize single resource
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to create a new category inline (for wizard workflows)
 * Includes optimistic update for instant UI feedback
 * Returns normalized category object for immediate use in wizard
 */
export const createCategoryInlineThunk = createAsyncThunk(
  "categories/createInline",
  async (data, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await categoriesApi.createCategory(data);

      // Process any included resources
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize single resource
      const normalized = normalizeData(jsonApiResponse.data);

      // Return with metadata for inline creation context
      return {
        ...normalized,
        _isInlineCreation: true,
        _createdAt: new Date().toISOString()
      };
    } catch (error) {
      // Enhanced error parsing for wizard validation
      let errorMessage = error.apiError?.message || error.message;

      // Check for duplicate name error (common validation error)
      if (error.response?.data?.errors) {
        const nameError = error.response.data.errors.find(e =>
          e.source?.pointer === '/data/attributes/name' || e.detail?.includes('name')
        );
        if (nameError) {
          errorMessage = 'Category name already exists';
        }
      }

      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Async thunk to update an existing category
 * Normalizes JSON:API response and processes included resources
 */
export const updateCategoryThunk = createAsyncThunk(
  "categories/update",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await categoriesApi.updateCategory(id, data);

      // Process any included resources
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize single resource
      return normalizeData(jsonApiResponse.data);
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to delete a category
 */
export const deleteCategoryThunk = createAsyncThunk(
  "categories/delete",
  async (id, { rejectWithValue }) => {
    try {
      await categoriesApi.deleteCategory(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Async thunk to reorder categories
 * Normalizes JSON:API response and processes included resources
 */
export const reorderCategoriesThunk = createAsyncThunk(
  "categories/reorder",
  async (orderedCategories, { rejectWithValue }) => {
    try {
      const jsonApiResponse = await categoriesApi.reorderCategories(orderedCategories);

      // Process any included resources
      const included = normalizeIncluded(jsonApiResponse.included || []);

      // Normalize array of resources
      return normalizeData(jsonApiResponse.data) || [];
    } catch (error) {
      return rejectWithValue(error.apiError?.message || error.message);
    }
  }
);

/**
 * Categories slice with entity adapter for normalized state management
 *
 * State structure:
 * {
 *   entities: { 'id1': {...}, 'id2': {...} },
 *   ids: ['id1', 'id2'],
 *   loading: false,
 *   error: null
 * }
 */
const categoriesSlice = createSlice({
  name: "categories",
  initialState: categoriesAdapter.getInitialState({
    loading: false,
    error: null,
    createInlineLoading: false,
    createInlineError: null,
  }),
  reducers: {
    /**
     * Clear inline creation error (for dismissing wizard errors)
     */
    clearInlineError: (state) => {
      state.createInlineError = null;
    },
    /**
     * Add a single category (from entity adapter)
     */
    addOne: categoriesAdapter.addOne,

    /**
     * Add multiple categories (from entity adapter)
     */
    addMany: categoriesAdapter.addMany,

    /**
     * Update a category (from entity adapter)
     */
    updateOne: categoriesAdapter.updateOne,

    /**
     * Update multiple categories (from entity adapter)
     */
    updateMany: categoriesAdapter.updateMany,

    /**
     * Upsert (add or update) a single category (from entity adapter)
     */
    upsertOne: categoriesAdapter.upsertOne,

    /**
     * Upsert (add or update) multiple categories (from entity adapter)
     * This is the key action for processing included resources!
     */
    upsertMany: categoriesAdapter.upsertMany,

    /**
     * Remove a category by ID (from entity adapter)
     */
    removeOne: categoriesAdapter.removeOne,

    /**
     * Remove multiple categories by ID (from entity adapter)
     */
    removeMany: categoriesAdapter.removeMany,

    /**
     * Set all categories (replaces entire collection)
     */
    setAll: categoriesAdapter.setAll,

    /**
     * Clear all categories (useful for tenant switching)
     */
    clearAll: (state) => {
      categoriesAdapter.removeAll(state);
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Categories
      .addCase(fetchCategoriesThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoriesThunk.fulfilled, (state, action) => {
        state.loading = false;
        categoriesAdapter.setAll(state, action.payload?.data || []);
      })
      .addCase(fetchCategoriesThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Category
      .addCase(createCategoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        categoriesAdapter.addOne(state, action.payload);
      })
      .addCase(createCategoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Create Category Inline
      .addCase(createCategoryInlineThunk.pending, (state) => {
        state.createInlineLoading = true;
        state.createInlineError = null;
      })
      .addCase(createCategoryInlineThunk.fulfilled, (state, action) => {
        state.createInlineLoading = false;
        categoriesAdapter.addOne(state, action.payload);
      })
      .addCase(createCategoryInlineThunk.rejected, (state, action) => {
        state.createInlineLoading = false;
        state.createInlineError = action.payload;
      })
      // Update Category
      .addCase(updateCategoryThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategoryThunk.fulfilled, (state, action) => {
        state.loading = false;
        categoriesAdapter.upsertOne(state, action.payload);
      })
      .addCase(updateCategoryThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Delete Category
      .addCase(deleteCategoryThunk.fulfilled, (state, action) => {
        categoriesAdapter.removeOne(state, action.payload);
      })
      .addCase(deleteCategoryThunk.rejected, (state, action) => {
        state.error = action.payload;
      })
      // Reorder Categories
      .addCase(reorderCategoriesThunk.fulfilled, (state, action) => {
        categoriesAdapter.upsertMany(state, action.payload);
      });
  },
});

// Export actions
export const {
  clearInlineError,
  addOne,
  addMany,
  updateOne,
  updateMany,
  upsertOne,
  upsertMany,
  removeOne,
  removeMany,
  setAll,
  clearAll,
} = categoriesSlice.actions;

// Export entity adapter selectors with safe defaults
export const {
  selectAll: selectAllCategories,
  selectById: selectCategoryById,
  selectIds: selectCategoryIds,
  selectEntities: selectCategoryEntities,
  selectTotal: selectCategoriesTotal,
} = categoriesAdapter.getSelectors((state) => state.categories || categoriesAdapter.getInitialState());

// Custom selectors
export const selectCategoriesLoading = (state) => state.categories?.loading || false;
export const selectCategoriesError = (state) => state.categories?.error || null;
export const selectCreateInlineLoading = (state) => state.categories?.createInlineLoading || false;
export const selectCreateInlineError = (state) => state.categories?.createInlineError || null;

/**
 * Select active categories only
 */
export const selectActiveCategories = createSelector(
  [selectAllCategories],
  (categories) => categories.filter(cat => cat.is_active !== false)
);

/**
 * Select categories sorted by sort_order (adapter already sorts, but explicit selector for clarity)
 */
export const selectCategoriesSorted = createSelector(
  [selectAllCategories],
  (categories) => [...categories] // Already sorted by adapter
);

/**
 * Select categories formatted for dropdown use in wizard
 * Returns: [{ id, name, description }]
 */
export const selectCategoriesForDropdown = createSelector(
  [selectActiveCategories],
  (categories) => categories.map(cat => ({
    id: cat.id,
    name: cat.name,
    description: cat.description || '',
  })).sort((a, b) => a.name.localeCompare(b.name))
);

/**
 * Select available categories for metric creation wizard
 * Active categories sorted alphabetically
 */
export const selectAvailableCategories = createSelector(
  [selectActiveCategories],
  (categories) => categories.sort((a, b) => a.name.localeCompare(b.name))
);

// Legacy selectors for backward compatibility
export const selectCategories = selectAllCategories;

// Export reducer
export default categoriesSlice.reducer;
